(() => {
  const revealSelector = '[data-scroll-reveal]';
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pendingItems = new Set();
  let observer;
  let scanQueued = false;

  const getRevealItems = (scope = document) => {
    const revealItems = [...scope.querySelectorAll(revealSelector)];

    if (scope instanceof Element && scope.matches(revealSelector)) {
      revealItems.unshift(scope);
    }

    return revealItems.filter((item) => !item.dataset.scrollRevealObserved);
  };

  const showItemsImmediately = (items) => {
    items.forEach((item) => {
      item.dataset.scrollRevealObserved = 'true';
      item.classList.add('is-visible');
      pendingItems.delete(item);
      observer?.unobserve(item);
    });
  };

  const revealItem = (item) => {
    item.classList.add('is-visible');
    pendingItems.delete(item);
    observer?.unobserve(item);
  };

  const scanViewport = () => {
    scanQueued = false;
    const revealLine = window.innerHeight * 0.86;

    pendingItems.forEach((item) => {
      const bounds = item.getBoundingClientRect();

      if (bounds.top <= revealLine && bounds.bottom >= 0) {
        revealItem(item);
      }
    });
  };

  const queueViewportScan = () => {
    if (scanQueued || reduceMotionQuery.matches) return;
    scanQueued = true;
    requestAnimationFrame(scanViewport);
  };

  const getObserver = () => {
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries, activeObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          pendingItems.delete(entry.target);
          entry.target.classList.add('is-visible');
          activeObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -14% 0px', threshold: 0.04 }
    );

    return observer;
  };

  const initializeReveal = (scope = document, waitForPaint = false) => {
    const revealItems = getRevealItems(scope);

    if (!revealItems.length) return;

    if (reduceMotionQuery.matches) {
      showItemsImmediately(revealItems);
      return;
    }

    const activeObserver = 'IntersectionObserver' in window ? getObserver() : null;

    revealItems.forEach((item) => {
      item.dataset.scrollRevealObserved = 'true';
      item.classList.remove('is-visible');
      pendingItems.add(item);
      activeObserver?.observe(item);
    });

    document.body.classList.add('has-scroll-reveal');

    if (waitForPaint) {
      requestAnimationFrame(() => requestAnimationFrame(queueViewportScan));
    } else {
      queueViewportScan();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeReveal(document, true), { once: true });
  } else {
    initializeReveal(document, true);
  }

  document.addEventListener('shopify:section:load', (event) => initializeReveal(event.target, true));
  window.addEventListener('scroll', queueViewportScan, { passive: true });
  window.addEventListener('resize', queueViewportScan, { passive: true });
  window.addEventListener('pageshow', queueViewportScan);

  const handleMotionPreference = (event) => {
    if (!event.matches) return;
    document.querySelectorAll(revealSelector).forEach((item) => item.classList.add('is-visible'));
    document.body.classList.remove('has-scroll-reveal');
    pendingItems.clear();
    observer?.disconnect();
    observer = undefined;
  };

  if ('addEventListener' in reduceMotionQuery) {
    reduceMotionQuery.addEventListener('change', handleMotionPreference);
  } else {
    reduceMotionQuery.addListener(handleMotionPreference);
  }
})();
